/**
 * WeightNumpad Component
 *
 * A POS-style numpad for rapid weight entry.
 * Features a large display and on-screen buttons that work
 * alongside physical keyboard/numpad input.
 *
 * Part of Quick Harvest Recording feature
 */

import { useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Delete } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Build weight string from key input
 * @param {string} prev - Current weight string
 * @param {string} key - Key pressed (0-9, '.', 'backspace', 'clear')
 * @returns {string} Updated weight string
 */
function buildWeightString(prev, key) {
  // Handle clear
  if (key === 'clear') {
    return ''
  }

  // Handle backspace
  if (key === 'backspace') {
    return prev.slice(0, -1)
  }

  // Handle decimal
  if (key === '.') {
    // Only one decimal allowed
    if (prev.includes('.')) return prev
    // Add leading zero if starting with decimal
    if (prev === '') return '0.'
    return prev + '.'
  }

  // Handle digits
  if (/^\d$/.test(key)) {
    const decimalIndex = prev.indexOf('.')
    if (decimalIndex !== -1) {
      const decimals = prev.length - decimalIndex - 1
      // At 3 decimal places already, reject
      if (decimals >= 3) return prev
      // 3rd decimal place can only be 0 or 5
      if (decimals === 2 && key !== '0' && key !== '5') return prev
    }
    // Prevent excessive length (max 6 chars: "99.995")
    if (prev.length >= 6) return prev
    // Prevent leading zeros unless followed by decimal
    if (prev === '0' && key !== '0') return key
    if (prev === '0' && key === '0') return prev
    return prev + key
  }

  return prev
}

const numpadKeys = [
  ['7', '8', '9'],
  ['4', '5', '6'],
  ['1', '2', '3'],
  ['.', '0', 'backspace']
]

export default function WeightNumpad({
  value = '',
  onChange,
  disabled = false,
  maxValue = 50,
  autoFocus = true
}) {
  // Handle key input (both numpad clicks and keyboard)
  const handleInput = useCallback((key) => {
    if (disabled) return
    const newValue = buildWeightString(value, key)
    onChange(newValue)
  }, [value, onChange, disabled])

  // Keyboard event listener for physical numpad
  useEffect(() => {
    if (disabled || !autoFocus) return

    const handleKeyDown = (e) => {
      // Ignore if focus is on an input element (like batch selector)
      if (e.target.tagName === 'INPUT' ||
          e.target.tagName === 'TEXTAREA' ||
          e.target.closest('[role="listbox"]') ||
          e.target.closest('[role="combobox"]')) {
        return
      }

      // Number keys (both main keyboard and numpad)
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault()
        handleInput(e.key)
        return
      }

      // Decimal point (. or , for international keyboards)
      if (e.key === '.' || e.key === 'Decimal' || e.key === ',') {
        e.preventDefault()
        handleInput('.')
        return
      }

      // Backspace
      if (e.key === 'Backspace') {
        e.preventDefault()
        handleInput('backspace')
        return
      }

      // Delete to clear all
      if (e.key === 'Delete') {
        e.preventDefault()
        handleInput('clear')
        return
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [disabled, autoFocus, handleInput])

  // Format display value
  const displayValue = value || '0.00'
  const numericValue = parseFloat(value) || 0
  const isOverMax = numericValue > maxValue

  return (
    <div className="space-y-4">
      {/* Large Weight Display */}
      <div
        className={cn(
          "text-center py-6 bg-surface-secondary rounded-lg border transition-colors",
          isOverMax ? "border-red-300 bg-red-50" : "border-border"
        )}
        role="status"
        aria-live="polite"
      >
        <span
          className={cn(
            "text-5xl font-semibold tabular-nums tracking-tight",
            isOverMax ? "text-red-600" : "text-content-primary"
          )}
        >
          {displayValue}
        </span>
        <span
          className={cn(
            "text-2xl ml-2",
            isOverMax ? "text-red-400" : "text-content-secondary"
          )}
        >
          kg
        </span>
        {isOverMax && (
          <p className="text-sm text-red-600 mt-2">
            Exceeds maximum ({maxValue}kg)
          </p>
        )}
      </div>

      {/* Numpad Grid */}
      <div className="grid grid-cols-3 gap-2">
        {numpadKeys.flat().map((key) => (
          <Button
            key={key}
            type="button"
            variant="outline"
            disabled={disabled}
            onClick={() => handleInput(key)}
            className={cn(
              "h-14 text-xl font-medium transition-colors",
              "hover:bg-gray-100 active:bg-gray-200",
              "focus:outline-none focus:ring-2 focus:ring-primary/20",
              key === 'backspace' && "text-content-secondary"
            )}
            aria-label={key === 'backspace' ? 'Clear last digit' : key === '.' ? 'Decimal point' : key}
          >
            {key === 'backspace' ? (
              <Delete className="h-5 w-5" />
            ) : (
              key
            )}
          </Button>
        ))}
      </div>

      {/* Clear button */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={disabled || !value}
        onClick={() => handleInput('clear')}
        className="w-full text-content-secondary hover:text-content-primary"
      >
        Clear
      </Button>
    </div>
  )
}
